import React, { useRef, useMemo , createRef, useEffect}from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { format, add, sub } from 'date-fns'
import { v4 as uuidv4 } from 'uuid';
import { useDispatch, useSelector } from 'react-redux'

import { Paper, Button } from 'components'

import { timezoneDate } from 'utils'
import classes from './TimeSelector.module.css';
import moment from 'moment-timezone';

const TimePickerElement = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0.3rem 0.8rem;
    background-color: ${props => props.invalid ? null : props.selected ? 'var(--focus-color)' : 'var(--secondary-color)'};
    color: ${props => props.selected ? 'white' : 'var(--text-color)'};
    filter: ${props => props.invalid ? 'brightness(0.6)' : null};
    cursor: ${props => props.invalid ? 'not-allowed' : 'pointer'};
    border-radius: 5px;
    :hover {
        filter: ${props => !props.invalid ? 'brightness(0.9)' : null};
    }    
`

const SessionButton = styled.div`
    flex-grow:2;
    text-align: center;
    border-radius: ${props => props.borderRadius};
    background-color: ${props => props.backgroundColor || 'var(--secondary-color)'};
    padding: 0.2rem 0.5rem;
    border: 1px solid var(--primary-color);
    cursor: pointer;
    font-weight: bolder;
    font-size: 0.8rem;
    :hover{
        filter: brightness(var(--brightness));
    }
`

const Container = styled.div`
    background-color: var(--secondary-color);    
    display: flex;
    flex-direction: column;
    font-size: 0.9rem;
    border-radius: 5px;
    padding: ${props => props.padding};
`

const TimeSelector = props => {
    const {
        title,
        value,
        onSelect,
        onClear,
        onClose,
        inline,
        filter
    } = props;
    const user = useSelector((state) => state.auth.user)
    const timezone = user && user['timezone']
    const [time, selectTime] = React.useState(value ? moment(value).tz(timezone) : moment().tz(timezone));
    const ref = useRef();
    const hourRefs= useMemo(
        () => Array.from({ length: 13 }).map(() => createRef()),[]
    );
    const minuteRefs= useMemo(
        () => Array.from({ length: 60 }).map(() => createRef()),[]
    );

    useEffect(() => {
        // Round to closest 5 minutes
        let updatedTime = time
        updatedTime.minutes(Math.floor(time.minutes()/5) * 5)
        selectTime(updatedTime)
        onSelect(updatedTime)
        const hour = updatedTime.hours()
        const minutes = updatedTime.minutes()
        setTimeout(() => { // Cannot scroll wtih smooth behavior, chrome bug
            hourRefs[hour > 12 ? hour - 12 : hour].current && hourRefs[hour > 12 ? hour - 12 : hour].current.scrollIntoView({ block: 'start'})
            minuteRefs[minutes].current && minuteRefs[minutes].current.scrollIntoView({ block: 'start'})
        }, 0)
    }, [])

    const selectTimeHandler = (value, type) => {     
        if(!value & !type){
            selectTime(value)
            onSelect && onSelect(value);
        }
        else if(value >= 0 || ['am','pm'].includes(value)){
            let updatedTime = timezoneDate(time, timezone)
            updatedTime.seconds(0)
            if(type === 'hour'){
                const h = updatedTime.hours();
                const updatedValue = h >= 12 && value != 12 ? value + 12 
                                    : h < 12 && value == 12 ? 0
                                    : value
                updatedTime.hours(updatedValue);
                hourRefs[value].current.scrollIntoView({ behavior: 'smooth', block: 'start'})
            } else if(type === 'minute'){
                updatedTime.minutes(value);
                minuteRefs[value].current.scrollIntoView({ behavior: 'smooth', block: 'start'})
    
            } else if(type === 'session'){
                const h = updatedTime.hours();
                if(h < 12 && value === 'pm'){
                    updatedTime = updatedTime.add({hours: 12})
                } else if (h>= 12 && value == 'am') {
                    updatedTime = updatedTime.subtract({hours: 12})
                }
            }
            console.log(updatedTime.toDate())
            selectTime(updatedTime);
            onSelect && onSelect(updatedTime);
        } 
    }

    const clearHandler = () => {
        selectTimeHandler(null, null)
        selectTime(moment().tz(timezone))
        props.closeHandler && props.closeHandler()
        onSelect && onSelect(null)
    }

    const renderInput = () => {
        // Constants
        let filterTime = moment().tz(timezone)
        if(filter){
            // const f = new Date(filter.time)
            const f = moment(filter.time).tz(timezone)
            filterTime.hours(f.hours())
            filterTime.minutes(f.minutes())
            filterTime.seconds(0)
            filterTime.milliseconds(0)
            const filterMethod = filter ? filter.how : null
            // console.log({filterTime:filterTime, filterMethod: filterMethod, time: time})
        }
        

        // Hour elements
        let hourElements = [];
        for (var i = 1; i < 13; i++) {
            const k = i
            const hour = time.hours()
            const filterHour = filterTime.hours()
            const elementHour = hour > 12 ? k + 12 : k
            const invalid = !filter ? false
                            : filter.how === 'after' ? elementHour > filterHour
                            : filter.how === 'before' ? elementHour < filterHour
                            : false
            // console.log(k, filterTime.getHours())
            hourElements.push(
                <TimePickerElement ref={hourRefs[k]} key={uuidv4()}
                    invalid={invalid}
                    onClick={() => !invalid && selectTimeHandler(k, 'hour')} 
                    selected={time && time.hour() > 12 ? k === time.hour() - 12 
                        : time && time.hour() === 0 ? k === 12
                        : time && k === time.hour()}
                    >
                    {k}
                </TimePickerElement>
            )
        }

        // Minute elements
        let minuteElements = [];
        for (var j = 0; j < 60; j += 5) {
            const k = j
            let elementTime = moment().tz(timezone)
            elementTime.minutes(k)
            elementTime.hours(time.hours())
            elementTime.seconds(0)
            elementTime.milliseconds(0)
            const invalid = !filter ? false
                            : filter.how === 'after' ? elementTime.isAfter(filterTime)
                            : filter.how === 'before' ? elementTime.isBefore(filterTime)
                            : false
            minuteElements.push(
                <TimePickerElement ref={minuteRefs[k]} key={uuidv4()}
                    invalid={invalid}
                    onClick={() => !invalid && selectTimeHandler(k, 'minute')} 
                    selected={time && time.minute() === k}>
                    {k}
                </TimePickerElement>
            )
        }
        
        const element = <div className={classes.TimePickerSelectors}>
                            <div className={classes.TimePickerColumn}>
                                {hourElements}
                            </div>
                            <div className={classes.TimePickerColumn}>
                                {minuteElements}
                            </div>
                        </div>
        
        return element
    }
    const content = <Container>
                        {/* AM PM */}
                        <div className={classes.TimePickerAMPM}>
                            <SessionButton onClick={() => selectTimeHandler('am','session')} backgroundColor={time.hour() < 12 ? 'var(--primary-color)' : null} borderRadius='5px 0 0 5px'>AM</SessionButton>
                            <SessionButton onClick={() => selectTimeHandler('pm','session')} backgroundColor={time.hour() >= 12 ? 'var(--primary-color)' : null} borderRadius='0 5px 5px 0'>PM</SessionButton>
                        </div>

                        {/* Selector */}
                        {renderInput()}

                        {/* Footer */}
                        <Button text='Clear' styles={{backgroundColor: 'var(--primary-color)', fontWeight: 'bolder'}} onSelect={clearHandler} margin='0.5rem 0 0 0'/>
                    </Container>
    
    if(inline){
        return (
            <div>{content}</div>
        )
    } else {
        return (
            <Paper margin='0.5rem 0'>
                <Container padding='0.5rem'>{content}</Container>
            </Paper>
        )
    }
}

export default TimeSelector;

TimeSelector.propTypes = {
    filter: PropTypes.object
}